// src/pages/Statements/EmployeeStatements.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import Toast from '../../components/Toast/Toast';
import Pagination from './Pagination';
import './Statements.css';

const PAGE_SIZE = 15;

const EmployeeStatements = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, user } = useAuth();
  const { selectedCompany } = useCompany();

  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  const [toast,     setToast]     = useState(null);

  const companyId = isSuperAdmin ? selectedCompany?.id : user?.companyId;

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }

    setLoading(true);
    const url = isSuperAdmin
      ? `/tours/employees-with-tours?companyId=${companyId}`
      : `/tours/employees-with-tours`;

    api.get(url)
      .then(res => {
        const list = (res.data.data || []).map(e => ({
          ...e,
          latestTour: e.latestTour ? new Date(e.latestTour) : null,
        }));
        setEmployees(list);
      })
      .catch(err => {
        setToast({
          message: err.response?.data?.message || 'Failed to load employees',
          type: 'error',
        });
      })
      .finally(() => setLoading(false));
  }, [companyId, isSuperAdmin]);

  const filtered = useMemo(() => {
    if (!search.trim()) return employees;
    const s = search.toLowerCase();
    return employees.filter(e =>
      `${e.firstName} ${e.lastName}`.toLowerCase().includes(s) ||
      (e.employeeCode || '').toLowerCase().includes(s)
    );
  }, [employees, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const viewStatement = (emp) => {
    navigate(`/statements/employee/${emp.userId}`, {
      state: {
        employeeName: `${emp.firstName} ${emp.lastName}`.trim() || `Employee ${emp.employeeCode}`,
      },
    });
  };

  return (
    <div className="stmt-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb" className="stmt-breadcrumb">
        <span>Dashboard</span>
        <span>›</span>
        <span className="active">Tour Statements</span>
      </nav>

      <div className="stmt-header">
        <div>
          <h4 className="stmt-title">Tour Statements</h4>
          <p className="stmt-subtitle">
            Employee tour and travel statements — computed from actual tour entries
          </p>
        </div>
      </div>

      {isSuperAdmin && !companyId && (
        <div className="stmt-warning-banner">
          ⚠️ Please select a company from the top navbar to view statements.
        </div>
      )}

      <div className="stmt-card">
        <div className="stmt-toolbar">
          <div className="stmt-search-wrap">
            <span className="stmt-search-icon">🔍</span>
            <input
              type="text"
              className="stmt-search-input"
              placeholder="Search employee by name or code..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span className="stmt-count">
            {loading ? 'Loading...' : `${filtered.length} employee${filtered.length === 1 ? '' : 's'}`}
          </span>
        </div>

        {loading ? (
          <div className="stmt-loading">Loading tour statements...</div>
        ) : filtered.length === 0 ? (
          <div className="stmt-empty">
            <div className="stmt-empty-icon">📋</div>
            <p className="stmt-empty-title">
              {search ? `No employees match "${search}"` : 'No tour entries found'}
            </p>
            <p className="stmt-empty-sub">
              {isSuperAdmin && !companyId
                ? 'Select a company from the navbar to begin'
                : 'Employees will show here once they log tours'}
            </p>
          </div>
        ) : (
          <>
            <div className="stmt-table-wrap">
              <table className="stmt-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>No</th>
                    <th>Employee Name</th>
                    <th style={{ width: 140 }}>Latest Tour</th>
                    <th style={{ width: 100 }}>Tours</th>
                    <th style={{ width: 100 }}>Status</th>
                    <th style={{ width: 160 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((emp, idx) => (
                    <tr key={emp.userId}>
                      <td className="stmt-td-num">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                      <td>
                        <div className="stmt-employee-cell">
                          <div className="stmt-avatar">
                            {(emp.firstName?.[0] || emp.employeeCode?.[0] || '?').toUpperCase()}
                          </div>
                          <div>
                            <p className="stmt-employee-name">
                              {emp.firstName || emp.lastName
                                ? `${emp.firstName} ${emp.lastName}`.trim()
                                : `Employee ${emp.employeeCode || emp.userId}`}
                            </p>
                            {emp.employeeCode && (
                              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6c757d' }}>
                                Code: {emp.employeeCode}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="stmt-td-date">
                        {emp.latestTour ? emp.latestTour.toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="stmt-td-count">{emp.tourCount}</td>
                      <td>
                        <span className="stmt-badge stmt-badge-working">WORKING</span>
                      </td>
                      <td>
                        <button className="stmt-btn-view" onClick={() => viewStatement(emp)}>
                          View Statement
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={filtered.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeStatements;